import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Glyph } from './glyphs';
import { cn } from '../../lib/cn';
import { MARKET_TABS, isNavItemActive } from './navModel';
import { useCart } from '../../../Contexts/CartContext';
import { useAuth } from '../../../hooks/useAuth';
import { useFavourites } from '../../../favourites/useFavourites';

/**
 * The floating capsule tab bar, mobile only. THE app nav below 1024.
 *
 * Above 1024 this does not exist: the desktop track replaces it with the
 * MarketShell top nav or the ConsoleShell sidebar (DESKTOP-PREFERENCES section
 * 5). It is not hidden with CSS, it is never mounted, which is exactly why
 * MarketShell branches on `useViewport()` rather than a media query.
 *
 * Active is an ink pill invert, driven by `aria-current="page"` in the vendored
 * CSS, so the accessible state and the visual state cannot drift apart. The
 * glyph fills at the same moment for the same reason.
 *
 * Badges are counts of live state, and BOTH read that state here rather than
 * being handed it. `badge: 'cart'` reads CartContext; `badge: 'wishlist'` reads
 * `useFavourites`, which is the same store every heart in the app toggles.
 *
 * The wishlist one used to come down as a `wishlistCount` prop from the shell,
 * and only three pages ever passed it -- so the badge read 12 on a card detail
 * and nothing at all on /marketplace, the same number disagreeing with itself
 * two taps apart. A count of live state cannot be a prop that most callers
 * forget; the cart badge was already right about this and the collection badge
 * now matches it.
 *
 * `useFavourites` is a shared React Query key with a 5-minute staleTime, so the
 * surfaces that already call it (browse, both card details, the collection)
 * cost nothing extra and the rest join the same cached read.
 *
 * @param {{
 *   items?: Array<object>,
 *   activeId?: string,
 *   label?: string,
 *   className?: string
 * }} props
 */
export function TabBar({ items = MARKET_TABS, activeId, label = 'Primary', className }) {
  const { pathname } = useLocation();
  const cart = useCart();
  const { isAuthenticated } = useAuth();
  const favourites = useFavourites();
  const counts = { cart: cart?.itemCount || 0, wishlist: favourites?.count || 0 };

  return (
    <nav className={cn('ds-tabbar', className)} aria-label={label}>
      {/* Same `authOnly` rule as the desktop top nav, read from the same nav
          model, so the two tracks cannot disagree about what a signed out
          visitor is offered. */}
      {items
        .filter((item) => !item.authOnly || isAuthenticated)
        .map((item) => {
          const active = activeId ? activeId === item.id : isNavItemActive(item, pathname);
          const count = item.badge ? counts[item.badge] || 0 : 0;

          return (
            <Link
              key={item.id}
              to={item.to}
              className="ds-tabbar__item"
              aria-current={active ? 'page' : undefined}
              aria-label={count > 0 ? `${item.label}, ${count} items` : undefined}
            >
              <span className="ds-tabbar__iconwrap">
                <Glyph name={item.glyph} filled={active} />
                {count > 0 && (
                  <span className="ds-tabbar__badge" aria-hidden="true">
                    {count}
                  </span>
                )}
              </span>
              <span>{item.label}</span>
            </Link>
          );
        })}
    </nav>
  );
}

export default TabBar;
